'use strict';

const { drawBossProjectile } = require('../ui/boss-projectile-renderers');

class BossProjectile {
  constructor() {
    this.active = false;
    this.profile = null;
  }

  reset(options) {
    const source = options || {};
    const profile = source.profile || {};
    this.active = true;
    this.profile = profile;
    this.x = Number(source.x) || 0;
    this.y = Number(source.y) || 0;
    this.heading = Number.isFinite(source.heading) ? source.heading : Math.PI / 2;
    this.speed = Math.max(0, Number(source.speed) || 0);
    this.radius = Math.max(4, Number(source.radius) || Number(profile.radius) || 12);
    this.motionKey = source.motionKey || profile.motionKey || 'straight';
    this.amplitude = Number(source.amplitude ?? profile.amplitude) || 0;
    this.frequency = Number(source.frequency ?? profile.frequency) || 0;
    this.turnRate = Math.max(0, Number(source.turnRate ?? profile.turnRate) || 0);
    this.acceleration = Number(source.acceleration ?? profile.acceleration) || 0;
    this.curveRate = Number(source.curveRate ?? profile.curveRate) || 0;
    this.turnAfter = Math.max(0, Number(source.turnAfter ?? profile.turnAfter) || 0);
    this.rotationSpeed = Number(source.spin ?? source.rotationSpeed ?? profile.rotationSpeed) || 0;
    this.rotation = 0;
    this.elapsed = 0;
    this.phase = ((Number(profile.level) || 1) * 0.71) % (Math.PI * 2);
    this.splitAfter = Math.max(0, Number(source.splitAfter ?? profile.splitAfter) || 0);
    this.splitCount = Math.max(0, Math.floor(Number(source.splitCount ?? profile.splitCount) || 0));
    this.splitDone = Boolean(source.splitDisabled);
    this.pulse = 1;
    return this;
  }

  update(dt, bounds, target) {
    if (!this.active) return null;
    this.elapsed += dt;
    const motion = this.motionKey;

    if (motion === 'accelerate') this.speed = Math.max(40, this.speed + this.acceleration * dt);
    if (motion === 'arc' || motion === 'spiral') this.heading += this.curveRate * dt * (motion === 'spiral' ? 1.8 : 1);
    if (motion === 'boomerang' && this.elapsed >= this.turnAfter) this.heading += this.curveRate * 2.8 * dt;
    if (motion === 'homing' && target && this.turnRate > 0) this.turnToward(target, dt);

    let lateralSpeed = 0;
    if (motion === 'wave' || motion === 'drift') {
      lateralSpeed = Math.sin(this.elapsed * this.frequency + this.phase) * this.amplitude;
    } else if (motion === 'zigzag') {
      lateralSpeed = (Math.sin(this.elapsed * this.frequency + this.phase) >= 0 ? 1 : -1) * this.amplitude;
    }

    this.x += Math.cos(this.heading) * this.speed * dt;
    this.y += Math.sin(this.heading) * this.speed * dt;
    if (lateralSpeed) {
      this.x += Math.cos(this.heading + Math.PI / 2) * lateralSpeed * dt;
      this.y += Math.sin(this.heading + Math.PI / 2) * lateralSpeed * dt;
    }
    this.rotation += this.rotationSpeed * dt;
    this.pulse = motion === 'pulse' ? 1 + Math.sin(this.elapsed * this.frequency) * 0.16 : 1;

    if (!this.splitDone && this.splitAfter > 0 && this.elapsed >= this.splitAfter) {
      this.splitDone = true;
      this.active = false;
      return {
        x: this.x,
        y: this.y,
        heading: this.heading,
        count: this.splitCount,
        speed: this.speed,
        radius: this.radius,
        profile: this.profile
      };
    }

    const margin = this.radius + 110;
    if (
      this.x < bounds.left - margin || this.x > bounds.right + margin
      || this.y < bounds.top - margin || this.y > bounds.bottom + margin
    ) this.active = false;
    return null;
  }

  turnToward(target, dt) {
    const desired = Math.atan2(target.y - this.y, target.x - this.x);
    const difference = Math.atan2(Math.sin(desired - this.heading), Math.cos(desired - this.heading));
    const maximumTurn = this.turnRate * dt;
    this.heading += Math.max(-maximumTurn, Math.min(maximumTurn, difference));
  }

  draw(ctx) {
    if (!this.active) return;
    const originalRadius = this.radius;
    this.radius *= this.pulse;
    drawBossProjectile(ctx, this);
    this.radius = originalRadius;
  }

  getHitCircle() {
    return { x: this.x, y: this.y, radius: this.radius * 0.7 };
  }
}

module.exports = BossProjectile;
